import MaximizeGlyph from '../icons/MaximizeGlyph';
import useStyles, { VISUALIZATION_OVERLAY_CLASS } from './MessageBubble.styles';

export type VisualizationImageThumbnailProps = {
  /** HTTPS source of the visualization image. */
  src: string;
  /** Accessible alt text for the image. */
  alt: string;
  /** Accessible name for the expand affordance. */
  expandAriaLabel: string;
  /** Fired when the user activates the thumbnail to open the preview. */
  onExpand: () => void;
  /** Fired when the underlying image fails to load. */
  onError: () => void;
};

/**
 * A single visualization image rendered as a clickable thumbnail.
 *
 * On hover/focus a brand-100 overlay with a maximize glyph appears,
 * inviting the user to open the full-screen zoom preview.
 */
function VisualizationImageThumbnail({
  src,
  alt,
  expandAriaLabel,
  onExpand,
  onError,
}: VisualizationImageThumbnailProps) {
  const { classes, cx } = useStyles();

  return (
    <button
      type="button"
      className={classes.visualizationThumbnail}
      aria-label={expandAriaLabel}
      onClick={onExpand}
      data-testid="ai-sidekick-message-visualization-thumbnail"
    >
      <img
        alt={alt}
        className={classes.visualizationImage}
        data-testid="ai-sidekick-message-visualization-image"
        decoding="async"
        loading="lazy"
        onError={onError}
        referrerPolicy="no-referrer"
        src={src}
      />
      <span
        aria-hidden="true"
        className={cx(
          classes.visualizationOverlay,
          VISUALIZATION_OVERLAY_CLASS
        )}
      >
        <MaximizeGlyph />
      </span>
    </button>
  );
}

export default VisualizationImageThumbnail;
